// Laravel Sanctum service for CSRF token handling
export class SanctumService {
  private static csrfInitialized = false;

  /**
   * Fetches the CSRF cookie from Laravel Sanctum
   * This must be called before making any authenticated requests
   */
  static async initCsrf(): Promise<void> {
    if (this.csrfInitialized) {
      return;
    }

    try {
      const response = await fetch(`/sanctum/csrf-cookie`, {
        method: 'GET',
        credentials: 'include', // Include cookies in the request
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CSRF cookie: ${response.status}`);
      }

      this.csrfInitialized = true;
      console.log('CSRF token initialized');
    } catch (error) {
      console.error('Failed to initialize CSRF token:', error);
      // Don't throw - allow app to continue even if CSRF init fails
      // This allows the app to work with local fallback data
    }
  }

  /**
   * Gets the CSRF token from the cookie
   */
  static getCsrfToken(): string | null {
    const name = 'XSRF-TOKEN';
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if (parts.length === 2) {
      const token = parts.pop()?.split(';').shift();
      return token ? decodeURIComponent(token) : null;
    }

    return null;
  }

  /**
   * Gets headers to include in API requests
   * Includes CSRF token and credentials configuration
   */
  static getHeaders(additionalHeaders?: HeadersInit): HeadersInit {
    const csrfToken = this.getCsrfToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...additionalHeaders,
    };

    if (csrfToken) {
      (headers as Record<string, string>)['X-XSRF-TOKEN'] = csrfToken;
    }

    return headers;
  }

  /**
   * Gets fetch options that include credentials and CSRF token
   */
  static getFetchOptions(options?: RequestInit): RequestInit {
    return {
      credentials: 'include', // Default: Always include cookies
      ...options, // Allow overriding credentials if needed
      headers: this.getHeaders(options?.headers), // Merge headers (not override)
    };
  }

  /**
   * Logs out the current user by making a POST request to /logout
   * Redirects to login page on success
   */
  static async logout(): Promise<void> {
    try {
      // Ensure CSRF token is initialized
      await this.initCsrf();

      const response = await fetch(`/logout`, {
        method: 'POST',
        ...this.getFetchOptions(),
      });

      // Laravel returns 204 No Content on successful logout
      if (response.ok || response.status === 204) {
        window.location.href = '/login';
      } else {
        throw new Error(`Logout failed: ${response.status}`);
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Still redirect to login even on error, as the user intended to log out
      window.location.href = '/login';
    }
  }
}
